import { ReactElement, useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-formV7";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Badge } from "@twilio-paste/core/badge";
import { ThumbsUpIcon } from "@twilio-paste/icons/esm/ThumbsUpIcon";
import { ThumbsDownIcon } from "@twilio-paste/icons/esm/ThumbsDownIcon";
import { SuccessIcon } from "@twilio-paste/icons/esm/SuccessIcon";
import { Label } from "@twilio-paste/core/label";
import { TextArea } from "@twilio-paste/core/textarea";
import { HelpText } from "@twilio-paste/core/help-text";
import {
  Callout,
  CalloutHeading,
  CalloutText,
} from "@twilio-paste/core/callout";
import { AIChatMessageActionCard } from "@twilio-paste/core/ai-chat-log";

import { useWiseOwlContext } from "../../hooks";
import { copy } from "./definitions";
import { ContactSupport } from "./sub";
import {
  CustomerFeedbackFormValues,
  FeedbackFormFields,
  FeedbackProps,
  dataTestIds,
  trackingDetails,
  WiseOwlIngress,
  WiseOwlElementIds,
  FeedbackChoice,
} from "@twiliointernal/e2-commons-shared-definitions";
import styles from "./css/style.module.css";
import { useScroll } from "@twiliointernal/e2-commons-shared-hooks";

const Feedback = ({
  block: {
    values: { messageId, supportCtaChannels, selected },
  },
}: FeedbackProps): ReactElement => {
  // We only support one channel for now. One day we may support multiple.
  const supportCtaChannel = supportCtaChannels.length
    ? supportCtaChannels[0]
    : null;

  const {
    chatId,
    isLoggedIn,
    ingress,
    handlers: { handleSubmitFeedback, handleTrackEvent },
    applicationProps: { displayName, scrollContainerElementId },
  } = useWiseOwlContext();

  const wiseOwlScrollContainer = useRef<HTMLElement>(
    document.querySelector(
      `[data-paste-element="${
        scrollContainerElementId ||
        WiseOwlElementIds.WISE_OWL_CHAT_LOG_CONTAINER
      }"]`
    )
  );
  const { updateScrollBottom } = useScroll(wiseOwlScrollContainer.current);

  const [loading, setLoading] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(false);
  //This is primarily for driving highlighting of thumbs up/down vs. form value
  const [isPositiveFeedback, setIsPositiveFeedback] = useState<boolean | null>(
    null
  );
  const [sendFeedbackSuccess, setSendFeedbackSuccess] = useState<boolean>();
  const [hasError, setHasError] = useState<boolean>();

  // form and field initialization
  const { handleSubmit, control, watch, reset, formState } =
    useForm<CustomerFeedbackFormValues>();
  const { isSubmitting, isSubmitted, errors } = formState;
  const feedbackFieldValueHasMinLengthError =
    errors?.feedback?.type === "minLength";
  const feedbackFieldValue = watch(FeedbackFormFields.Feedback);

  // We want to persist the contact form if:
  // -  Form presented to non-support users
  //  - Feedback not submitted: the Feedback UI is expanded
  //  - Feedback was submitted: The feedback was negative
  const isZendeskIngress = ingress === WiseOwlIngress.zendesk;
  const shouldRenderContactForm = isZendeskIngress
    ? false
    : sendFeedbackSuccess
    ? !isPositiveFeedback
    : isExpanded;

  // Check if the user has already submitted feedback. Update
  // state accordingly if so.
  useEffect(() => {
    if (!selected) {
      return;
    }
    setIsPositiveFeedback(selected === FeedbackChoice.ThumbsUp);
    setSendFeedbackSuccess(true);
  }, [selected]);

  // helps enforce validation on submit
  useEffect(() => {
    if (isSubmitted) {
      reset(undefined, { keepDirtyValues: true });
    }
  }, [feedbackFieldValue]);

  const handleSubmitFeedbackTrackingEvent = (eventName: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(eventName, {
        isPositiveFeedback,
        chatId,
        ingress,
        feedbackFieldValue,
      });
    }
  };

  // handlers
  const handleSubmitPositiveFeedback = async () => {
    if (sendFeedbackSuccess) {
      return;
    }

    setHasError(false);
    setIsPositiveFeedback(true);
    setIsExpanded(false);
    handleSubmitFeedbackTrackingEvent(
      trackingDetails.wiseOwl.positiveFeedbackClicked(displayName)
    );

    try {
      if (handleSubmitFeedback) {
        setLoading(true);
        setSendFeedbackSuccess(true);
        await handleSubmitFeedback({ messageId, positive: true, feedback: "" });

        handleSubmitFeedbackTrackingEvent(
          trackingDetails.wiseOwl.positiveFeedbackSent(displayName)
        );
      }

      setTimeout(updateScrollBottom, 100);
    } catch (err) {
      setSendFeedbackSuccess(false);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNegativeFeedback = async (
    data: CustomerFeedbackFormValues
  ) => {
    if (sendFeedbackSuccess) {
      return;
    }

    try {
      setHasError(false);
      if (handleSubmitFeedback) {
        setLoading(true);
        setIsExpanded(false);
        setSendFeedbackSuccess(true);
        await handleSubmitFeedback({
          messageId,
          positive: false,
          feedback: data.feedback,
        });

        handleSubmitFeedbackTrackingEvent(
          trackingDetails.wiseOwl.negativeFeedbackSent(displayName)
        );
      }
    } catch (err) {
      setSendFeedbackSuccess(false);
      setIsExpanded(true);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbsDown = () => {
    setHasError(false);
    setIsPositiveFeedback(false);
    setIsExpanded(true);
    setTimeout(updateScrollBottom, 100);

    handleSubmitFeedbackTrackingEvent(
      trackingDetails.wiseOwl.negativeFeedbackClicked(displayName)
    );
  };

  const handleCancelFeedback = () => {
    setHasError(false);
    setIsPositiveFeedback(null);
    setIsExpanded(false);

    handleSubmitFeedbackTrackingEvent(
      trackingDetails.wiseOwl.cancelNegativeFeedback(displayName)
    );
  };

  const renderErrorCallout = () => {
    return (
      <Box marginY="space50">
        <Callout variant="error">
          <CalloutHeading as="h2">
            {copy.errors.feedbackSubmissionHeader}
          </CalloutHeading>
          <CalloutText>{copy.errors.feedbackSubmissionText}</CalloutText>
        </Callout>
      </Box>
    );
  };

  return (
    <Box element={WiseOwlElementIds.WISE_OWL_FEEDBACK}>
      {/* Thumbs Up & Down */}
      <Box
        as="div"
        display="flex"
        alignItems="center"
        columnGap={["space10", "space40"]}
      >
        <AIChatMessageActionCard aria-label={copy.ariaLabel.feedbackAction}>
          {copy.ctas.feedback}
          <Button
            aria-label={copy.ariaLabel.positiveFeedback}
            disabled={sendFeedbackSuccess && isPositiveFeedback === false}
            onClick={handleSubmitPositiveFeedback}
            pressed={isPositiveFeedback === true}
            size="reset"
            variant="secondary_icon"
          >
            <ThumbsUpIcon decorative={false} title="Thumbs up" />
          </Button>
          <Button
            aria-label={copy.ariaLabel.negativeFeedback}
            disabled={sendFeedbackSuccess && isPositiveFeedback === true}
            onClick={handleThumbsDown}
            pressed={isPositiveFeedback === false}
            size="reset"
            variant="secondary_icon"
          >
            <ThumbsDownIcon decorative={false} title="Thumbs down" />
          </Button>
        </AIChatMessageActionCard>
      </Box>
      {/* Error call out (thumbs up) */}
      {hasError && isPositiveFeedback && renderErrorCallout()}
      {/* Feedback success badge j*/}
      {sendFeedbackSuccess && (
        <Box
          data-testid={dataTestIds.wiseOwlChat.feedbackSubmitted}
          display="flex"
          columnGap="space80"
          paddingY="space30"
          marginTop="space40"
        >
          <Badge as="span" variant="success">
            <SuccessIcon decorative />
            {copy.form.thumbsUpSuccess}
          </Badge>
        </Box>
      )}
      {!sendFeedbackSuccess && isExpanded && (
        <>
          {/* Feedback Form*/}
          <Box
            as="form"
            onSubmit={handleSubmit(handleSubmitNegativeFeedback)}
            className={styles.feedbackForm}
            borderWidth="borderWidth10"
            borderColor="colorBorderWeaker"
            borderStyle="solid"
            borderRadius="borderRadius20"
            padding="space60"
            marginTop="space50"
          >
            <Label htmlFor="message">{copy.fields.details}</Label>
            <Controller
              name={FeedbackFormFields.Feedback}
              control={control}
              rules={{ required: true, minLength: 10 }}
              render={({ field }): ReactElement => (
                <TextArea
                  data-testid={dataTestIds.wiseOwlChat.feedbackForm}
                  aria-describedby={copy.fields.ariaDescribedBy}
                  aria-label={copy.fields.ariaLabel}
                  disabled={loading}
                  required
                  {...field}
                />
              )}
            />
            {isSubmitted && feedbackFieldValueHasMinLengthError && (
              <HelpText variant="error">
                {copy.errors.feedbackMinLength}
              </HelpText>
            )}
            <Box
              marginTop={"space50"}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                variant="secondary"
                disabled={loading}
                onClick={handleCancelFeedback}
              >
                {copy.ctas.cancel}
              </Button>
              <Button
                variant="primary"
                disabled={!isSubmitting && !feedbackFieldValue}
                loading={loading}
                onClick={handleSubmit(handleSubmitNegativeFeedback)}
              >
                {copy.ctas.submit}
              </Button>
            </Box>
            {/* Error call out (thumbs down) */}
            {hasError && !isPositiveFeedback && renderErrorCallout()}
          </Box>
        </>
      )}
      {/* Contact Support CTAs */}
      {shouldRenderContactForm && supportCtaChannel !== null && (
        <ContactSupport isLoggedIn={isLoggedIn} channel={supportCtaChannel} />
      )}
    </Box>
  );
};

export default Feedback;
